import React from 'react';
import { useExperiment } from '@printdeal/feature-management/react';
import { ProductPageLayout } from './ProductPageLayout';
import { determineSelector } from './selector/helpers/SelectorHelper';
import ProductDetailEasy from './detail/easy/ProductDetailEasy';
import ProductDetailAdvanced from './detail/advanced/ProductDetailAdvanced';
import type { IPageData, IPortalConfiguration, IProductPage } from '../../types/product/productDetailPage';
import type { IPageContext } from '../../types/gatsby';

interface Props {
  data: {
    pageContent: IProductPage & IPageData;
    portalConfiguration: IPortalConfiguration,
  },
  localizedPaths: IPageContext['localizedPaths'],
  experimentId: string,
}

/**
 * This component is used to render the product page with the selector experiment.
 */
export const ProductPageSelectorExperiment = ({ data, localizedPaths, experimentId }: Props) => {
  const { pageContent: pageData } = data;
  const { resolvedSelectors, selectorExperiment, pageLayout } = pageData;

  // Only call useExperiment if we have a valid experiment ID
  const experimentResult = useExperiment(experimentId);
  const { experiment, isInitialized } = experimentResult;

  const selector = determineSelector(isInitialized, experiment, resolvedSelectors, selectorExperiment);

  // Easy Layout
  if (pageLayout === 'Easy') {
    return (
      <ProductPageLayout
        pageData={data}
        localizedPaths={localizedPaths}
        selector={selector}
        pageContent={pageData}
      >
        <ProductDetailEasy selector={selector} data={data} isSelectorInitialized={isInitialized} />
      </ProductPageLayout>
    );
  }

  // Advanced Layout
  return (
    <ProductPageLayout
      pageData={data}
      localizedPaths={localizedPaths}
      selector={selector}
      pageContent={pageData}
    >
      <ProductDetailAdvanced
        data={data}
        productPage={pageData}
        selector={selector}
        isSelectorInitialized={isInitialized}
      />
    </ProductPageLayout>
  );
};
